import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  Input,
  OnChanges,
  output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import isEqual from 'fast-deep-equal';
import { debounceTime, distinctUntilChanged, first, startWith, tap, timer } from 'rxjs';

import {
  ComparisonOperator,
  ComparisonQueryNode,
  LogicalQueryNode,
  LogicalType,
  QueryNode,
  QueryNodeType,
} from '@blaze-shared/api-contracts-domain';
import { deepCopyQueryNode, getAllComparisonNodes } from '@blaze-shared/datasets-domain';
import { TooltipDirective } from '@blaze-shared/shared-ui';

import {
  ComparisonQueryFormGroup,
  LogicalQueryFormGroup,
  LogicalQueryFormGroupValue,
  QueryRowDragDrop,
} from '../../complex-filter.models';
import {
  COMPLEX_FILTER_SERVICE,
  ComplexFilterService,
} from '../../providers/complex-filter.providers';
import { DragDropListService } from '../../providers/drag-drop-list.service';
import { LogicalQueryRowComponent } from '../logical-query-row/logical-query-row.component';

type RootFormGroup = FormGroup<{
  query: FormGroup<LogicalQueryFormGroup>;
}>;

@UntilDestroy()
@Component({
  selector: 'blaze-complex-filter',
  imports: [
    CommonModule,
    FormsModule,
    LogicalQueryRowComponent,
    MatTooltipModule,
    ReactiveFormsModule,
    TooltipDirective,
  ],
  templateUrl: './complex-filter.component.html',
  styleUrls: ['./complex-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DragDropListService],
})
export class ComplexFilterComponent implements OnChanges {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly complexFilterService = inject<ComplexFilterService>(COMPLEX_FILTER_SERVICE);
  private readonly fb = inject(NonNullableFormBuilder);

  lastRowDeleted = output();
  queryChange = output<{ query: LogicalQueryFormGroupValue }>();

  @Input() @HostBinding('class.with-theme') useAppTheme = false;
  @Input() disabled = false;
  @Input() isInOrganizeMode = false;
  @Input() layout: 'row' | 'column' = 'row';
  @Input() query: LogicalQueryNode | ComparisonQueryNode | undefined;

  form: RootFormGroup | null = null;
  organizeBtnDisabled = true;
  selectedFormGroups: (FormGroup<ComparisonQueryFormGroup> | FormGroup<LogicalQueryFormGroup>)[] =
    [];
  selectedLogicalFormGroups: FormGroup<LogicalQueryFormGroup>[] = [];
  busy = false;

  get columnLayout(): boolean {
    return this.layout === 'column';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['query']) {
      if (!this.form) {
        this.form = this.buildFormGroup();
      }

      this.toggleForm();
      this.form.valueChanges
        .pipe(
          tap(() => {
            this.busy = true;
            this.cdr.markForCheck();
          }),
          debounceTime(100),
          startWith(this.form.value),
          distinctUntilChanged(isEqual),
          untilDestroyed(this),
        )
        .subscribe((value: { query: LogicalQueryFormGroupValue }) => {
          this.selectedFormGroups = this.form?.controls.query.controls.children.controls.filter(
            (control) => control.value.selected,
          ) as any;

          this.selectedLogicalFormGroups = this.selectedFormGroups.filter(
            (formGroup) => formGroup.value.nodeType === QueryNodeType.Logical,
          ) as FormGroup<LogicalQueryFormGroup>[];

          this.organizeBtnDisabled =
            !value.query || getAllComparisonNodes(value.query as LogicalQueryNode).length < 2;
          this.emitFormChange(value.query);
          this.busy = false;
          this.cdr.markForCheck();
        });
    }

    if (changes['disabled']) {
      this.toggleForm();
    }
  }

  onItemDrop({ event, targetFormGroup }: QueryRowDragDrop): void {
    if (event.previousContainer === event.container && event.previousIndex === event.currentIndex) {
      return;
    } else if (event.previousContainer === event.container) {
      targetFormGroup.controls.children.removeAt(event.previousIndex);
      targetFormGroup.controls.children.insert(event.currentIndex, event.item.data);
    } else {
      const formArray = event.item.data.parent as FormArray;

      formArray.removeAt(event.previousIndex);

      this.removeFromParentIfEmpty(formArray);

      targetFormGroup.controls.children.insert(event.currentIndex, event.item.data);
    }
  }

  resetForm(): void {
    if (!this.form) {
      return;
    }

    for (const control of this.form.controls.query.controls.children.controls) {
      this.deleteRow(control);
    }
  }

  deleteRow(control: AbstractControl): void {
    if (control.parent instanceof FormArray) {
      control.parent.removeAt(control.parent.controls.indexOf(control));
      this.removeFromParentIfEmpty(control.parent);
    }

    if (this.form?.controls.query.controls.children.length === 0) {
      if (this.complexFilterService.afterDeletingLastRow === 'insert') {
        this.addNewRow(this.form);
      }

      // wait for the form to emit valueChange
      timer(150)
        .pipe(first())
        .subscribe(() => {
          this.lastRowDeleted.emit();
        });
    }
  }

  addNewRow(form: RootFormGroup): void {
    form.controls.query.controls.children.push(
      this.createComparisonFormGroup({
        nodeType: QueryNodeType.Comparison,
        conditionId: '',
        column: '',
        operator: ComparisonOperator.Eq,
        value: '',
        isFormulaValue: false,
        isOptional: false,
        displayInfos: [],
      }),
      { emitEvent: true },
    );
    form.controls.query.controls.children.updateValueAndValidity({
      onlySelf: false,
      emitEvent: true,
    });
    this.cdr.markForCheck();
  }

  breakGroups(): void {
    for (const formGroup of this.selectedLogicalFormGroups) {
      if (!(formGroup.parent instanceof FormArray)) {
        continue;
      }

      let control = formGroup.controls.children.controls[0];
      let index = formGroup.parent.controls.indexOf(formGroup);

      while (control) {
        formGroup.parent.insert(index, control);
        formGroup.controls.children.removeAt(0);
        control = formGroup.controls.children.controls[0];
        index++;
      }

      this.removeFromParentIfEmpty(formGroup.controls.children);
    }

    this.cdr.markForCheck();
  }

  groupNodes(): void {
    let selectedLogicalFormGroup = this.selectedLogicalFormGroups[0];

    if (!selectedLogicalFormGroup && this.form) {
      selectedLogicalFormGroup = this.createLogicalFormGroup({
        nodeType: QueryNodeType.Logical,
        logicalType: LogicalType.And,
        children: [],
      });
      this.form.controls.query.controls.children.insert(
        this.form.controls.query.controls.children.controls.indexOf(this.selectedFormGroups[0]),
        selectedLogicalFormGroup,
      );
    }

    for (const control of this.selectedFormGroups) {
      if (control === selectedLogicalFormGroup) {
        continue;
      }

      const parent = control.parent as FormArray;
      selectedLogicalFormGroup.controls.children.push(control);
      parent.removeAt(parent.controls.indexOf(control));
    }

    selectedLogicalFormGroup.patchValue({ selected: false });
    this.cdr.markForCheck();
  }

  toggleOrganizeMode(): void {
    this.isInOrganizeMode = !this.isInOrganizeMode;
  }

  rebuildForm(): void {
    if (!this.form) {
      this.form = this.buildFormGroup();
      return;
    }

    const newChildrenFormArray = this.buildFormGroup().controls.query.controls.children;
    const currentChildrenFormArray = this.form.controls.query.controls.children;

    currentChildrenFormArray.clear({ emitEvent: false });
    newChildrenFormArray.controls.forEach((control) => {
      currentChildrenFormArray.push(control, { emitEvent: false });
    });

    this.form.patchValue({ query: this.query }, { emitEvent: true });
    this.cdr.markForCheck();
  }

  private removeFromParentIfEmpty(formArray: FormArray): void {
    if (formArray.length === 0 && formArray.parent?.parent) {
      let parentControl: FormArray | FormGroup = formArray.parent?.parent;

      if (parentControl instanceof FormGroup && parentControl.controls['query']) {
        parentControl = parentControl.controls['query'] as FormArray;
      }

      if (parentControl instanceof FormArray && parentControl.controls) {
        parentControl.removeAt(parentControl.controls.indexOf(formArray.parent));
      }
    }
  }

  private buildFormGroup(): RootFormGroup {
    const node = this.normalizeInputToLogicalQueryNode();

    return this.fb.group({
      query: this.createLogicalFormGroup(node),
    });
  }

  private createLogicalFormGroup(node: LogicalQueryNode): FormGroup<LogicalQueryFormGroup> {
    return this.fb.group({
      nodeType: [QueryNodeType.Logical],
      logicalType: [node.logicalType],
      selected: [false],
      children: this.fb.array(
        node.children.map((child) => {
          if (this.isLogicalNode(child)) {
            return this.createLogicalFormGroup(child);
          } else if (this.isComparisonNode(child)) {
            return this.createComparisonFormGroup(child);
          }

          throw new Error('Unknown node type');
        }),
      ),
    });
  }

  private createComparisonFormGroup(
    node: ComparisonQueryNode,
  ): FormGroup<ComparisonQueryFormGroup> {
    return this.fb.group({
      formControlId: crypto.randomUUID() as string,
      nodeType: [QueryNodeType.Comparison],
      column: [node.column],
      operator: [node.operator],
      value: [node.value ?? ''],
      isOptional: [node.isOptional],
      isFormulaValue: [node.isFormulaValue],
      conditionId: [node.conditionId],
      selected: [false],
      displayInfos: [node.displayInfos],
    });
  }

  private normalizeInputToLogicalQueryNode(): LogicalQueryNode {
    if (!this.query) {
      return {
        nodeType: QueryNodeType.Logical,
        children: [
          {
            nodeType: QueryNodeType.Comparison,
            column: '',
            operator: ComparisonOperator.Eq,
            isOptional: false,
            value: '',
            conditionId: '',
            isFormulaValue: false,
            displayInfos: [],
          },
        ],
        logicalType: LogicalType.And,
      };
    }

    if (this.isLogicalNode(this.query) && this.query.children.length > 0) {
      return this.query;
    }

    if (this.isLogicalNode(this.query)) {
      return {
        ...this.query,
        children: [
          {
            nodeType: QueryNodeType.Comparison,
            column: '',
            operator: ComparisonOperator.Eq,
            isOptional: false,
            value: '',
            conditionId: '',
            isFormulaValue: false,
            displayInfos: [],
          },
        ],
      };
    }

    return {
      nodeType: QueryNodeType.Logical,
      children: [this.query],
      logicalType: LogicalType.And,
    };
  }

  private isComparisonNode(node: QueryNode): node is ComparisonQueryNode {
    return node.nodeType === QueryNodeType.Comparison;
  }

  private isLogicalNode(node: QueryNode): node is LogicalQueryNode {
    return node.nodeType === QueryNodeType.Logical;
  }

  private toggleForm(): void {
    if (!this.form) {
      return;
    }

    if (this.disabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  private emitFormChange(value: LogicalQueryFormGroupValue): void {
    const query = deepCopyQueryNode(value, (node) => {
      return (
        node.nodeType === QueryNodeType.Logical ||
        (node.nodeType === QueryNodeType.Comparison &&
          node.column?.length > 0 &&
          node.operator?.length > 0)
      );
    });
    this.queryChange.emit({ query });
  }
}
